import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';

export default function ExecutionDetailView({ executionId, alertId, onClose }) {
  const [execution, setExecution] = useState(null);
  const [alert, setAlert] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (executionId && alertId) {
      fetchExecutionDetails();
    }
  }, [executionId, alertId]);

  const fetchExecutionDetails = async () => {
    setLoading(true);
    try {
      // Fetch alert details
      const alertResponse = await axios.get(`/api/alerts/${alertId}`);
      setAlert(alertResponse.data);
      
      // Fetch execution history for this alert
      const historyResponse = await axios.get(`/api/alerts/${alertId}/history`);
      const execution = historyResponse.data.find(item => item.id === executionId);
      
      if (execution) {
        setExecution(execution);
      } else {
        toast.error('Execution details not found');
      }
    } catch (error) {
      console.error('Error fetching execution details:', error);
      toast.error('Failed to load execution details');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white shadow rounded p-6 animate-pulse">
        <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-4 bg-gray-200 rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  if (!execution || !alert) {
    return (
      <div className="bg-white shadow rounded p-6">
        <div className="text-center text-gray-500">
          Execution details not available
        </div>
        <div className="mt-4 flex justify-center">
          <button
            onClick={onClose}
            className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  const formatDuration = (seconds) => {
    if (!seconds) return '0s';
    
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    
    let result = '';
    if (hours > 0) result += `${hours}h `;
    if (minutes > 0) result += `${minutes}m `;
    if (remainingSeconds > 0 || result === '') result += `${remainingSeconds}s`;
    
    return result;
  };

  const calculateDuration = () => {
    if (!execution.run_start_time || !execution.run_end_time) return null;
    
    const start = new Date(execution.run_start_time);
    const end = new Date(execution.run_end_time);
    return (end - start) / 1000; // Duration in seconds
  };

  return (
    <div className="bg-white shadow rounded">
      <div className="bg-purple-l border-b border-gray-200 px-6 py-2 flex justify-between items-center">
        <h2 className="text-white font-bold tracking-wider uppercase text-md">Execution Details</h2>
        <button
          onClick={onClose}
          className="bg-white text-purple-600 hover:bg-gray-100 text-xs font-bold py-1 px-2 rounded focus:outline-none focus:shadow-outline"
        >
          Close
        </button>
      </div>
      
      <div className="p-6">
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-2">Alert Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50 p-4 rounded">
            <div>
              <p className="text-sm font-medium text-gray-500">Alert Name</p>
              <p className="mt-1 font-medium">{alert.name}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Alert ID</p>
              <p className="mt-1 font-mono">{alert.id}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Node Type</p>
              <p className="mt-1">{alert.node_type}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Schedule</p>
              <p className="mt-1">{alert.schedule_type.charAt(0).toUpperCase() + alert.schedule_type.slice(1)}</p>
            </div>
          </div>
        </div>
        
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-2">Execution Summary</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className={`p-4 rounded ${execution.status === 'success' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
              <p className="text-sm font-medium text-gray-500">Status</p>
              <p className={`mt-1 font-bold ${execution.status === 'success' ? 'text-green-700' : 'text-red-700'}`}>
                {execution.status.toUpperCase()}
              </p>
            </div>
            <div className="bg-purple-l border border-blue-200 p-4 rounded">
              <p className="text-sm font-medium text-gray-500">Affected Nodes</p>
              <p className="mt-1 font-bold text-gray-700">{execution.affected_nodes_count}</p>
            </div>
            <div className="bg-purple-l border border-purple-200 p-4 rounded">
              <p className="text-sm font-medium text-gray-500">Execution Time</p>
              <p className="mt-1 font-bold text-gray-700">{execution.execution_time_seconds}s</p>
            </div>
          </div>
        </div>
        
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-2">Execution Timeline</h3>
          <div className="bg-gray-50 p-4 rounded">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-gray-500">Start Time</p>
                <p className="mt-1">{new Date(execution.run_start_time).toLocaleString()}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">End Time</p>
                <p className="mt-1">{new Date(execution.run_end_time).toLocaleString()}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Duration</p>
                <p className="mt-1">{formatDuration(calculateDuration())}</p>
              </div>
            </div>
          </div>
        </div>
        
        {execution.error_message && (
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Error Details</h3>
            <div className="bg-red-50 border border-red-200 p-4 rounded">
              <p className="text-sm font-medium text-red-500 mb-2">Error Message</p>
              <pre className="whitespace-pre-wrap text-red-700 text-sm font-mono bg-red-100 p-3 rounded">
                {execution.error_message}
              </pre>
            </div>
          </div>
        )}
        
        <div>
          <h3 className="text-lg font-semibold text-gray-800 mb-2">Query Used</h3>
          <div className="bg-gray-50 p-4 rounded">
            <p className="text-sm font-medium text-gray-500 mb-2">Query ({alert.query_type.toUpperCase()})</p>
            <pre className="whitespace-pre-wrap text-gray-700 text-sm font-mono bg-gray-100 p-3 rounded overflow-auto max-h-64">
              {alert.query}
            </pre>
          </div>
        </div>
      </div>
    </div>
  );
} 